import { useCallback, useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.js'
import { useOverlay } from './overlay.js'

/**
 * A panel that slides up from the bottom of the screen.
 *
 * This is the phone's answer to `Modal`. A job on a phone is not a dialog
 * floating in the middle of a dimmed page — it is the screen, arriving over the
 * one you were on and leaving the same way, and it covers the bottom bar
 * because its own actions take that bar's place while it is open.
 *
 * Two sizes. `tall` stops a few pixels below the notch so a strip of the page
 * behind stays visible, which is what says this is a layer rather than a
 * navigation; `auto` takes only the room its contents need, up to most of the
 * screen, and is what the activity feed opens as on top of a `tall` one.
 *
 * Hand-written rather than a sheet library, for the same reason the dispatch
 * board owns its own pointer handling (decision 29): the whole behaviour is one
 * transform, a threshold and a timeout, and the drag has to release the
 * dismissal to the caller rather than unmounting from under it.
 */

/** Long enough to read as a movement, short enough not to be waited on. */
const EXIT_MS = 200

/** Past this, letting go dismisses rather than springs back. */
const DISMISS_PX = 96

export function Sheet({
  onClose,
  title,
  header,
  labelledBy,
  size = 'auto',
  scrollBody = true,
  className,
  children,
}: {
  onClose: () => void
  /** Shown in the header. Omitted when the body opens with its own title. */
  title?: string
  /** Drawn beside Done in place of a title, when the header says more than a name. */
  header?: ReactNode
  /** Names the dialog from an element inside it, when there is no `title`. */
  labelledBy?: string
  size?: 'tall' | 'auto'
  /**
   * Off when the children own their scrolling — the job card scrolls its body
   * and pins an action bar under it, which a scrolling wrapper would carry away.
   */
  scrollBody?: boolean
  className?: string
  children: ReactNode
}) {
  const [entered, setEntered] = useState(false)
  const [closing, setClosing] = useState(false)
  const [drag, setDrag] = useState(0)
  const from = useRef<number | null>(null)
  const exit = useRef<number | null>(null)

  // Animates out, then tells the caller. Guarded, because a drag that ends past
  // the threshold and a tap on the backdrop can both land in the same frame.
  const close = useCallback(() => {
    if (exit.current !== null) return
    setClosing(true)
    setDrag(0)
    exit.current = window.setTimeout(onClose, EXIT_MS)
  }, [onClose])

  useEffect(
    () => () => {
      if (exit.current !== null) clearTimeout(exit.current)
    },
    [],
  )

  const depth = useOverlay(true, close)

  // A frame after mount, so the browser has painted the panel off-screen and
  // has something to animate from. Setting it during the first render is a
  // sheet that is simply already open.
  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  const dragStart = (event: PointerEvent<HTMLDivElement>) => {
    from.current = event.clientY
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const dragMove = (event: PointerEvent<HTMLDivElement>) => {
    if (from.current === null) return
    setDrag(Math.max(0, event.clientY - from.current))
  }

  const dragEnd = () => {
    if (from.current === null) return
    from.current = null
    if (drag > DISMISS_PX) close()
    else setDrag(0)
  }

  const open = entered && !closing

  return createPortal(
    <div className="fixed inset-0 flex flex-col justify-end" style={{ zIndex: 50 + depth * 10 }}>
      <div
        aria-hidden="true"
        onPointerDown={close}
        className={cn(
          'absolute inset-0 bg-ink-900/40 transition-opacity duration-200',
          open ? 'opacity-100' : 'opacity-0',
        )}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-labelledby={title ? undefined : labelledBy}
        style={drag > 0 ? { transform: `translateY(${drag}px)`, transition: 'none' } : undefined}
        className={cn(
          'relative flex min-h-0 flex-col overflow-hidden rounded-t-2xl bg-white shadow-[0_-8px_30px_rgb(15_23_42_/_0.18)] transition-transform duration-200 ease-out',
          size === 'tall' ? 'h-[calc(100%-var(--safe-top)-8px)]' : 'max-h-[88dvh]',
          open ? 'translate-y-0' : 'translate-y-full',
          className,
        )}
      >
        {/* The grabber is the drag target as well as the sign that dragging
            works. Pointer events rather than touch, so it behaves the same
            when somebody drags it with a mouse on a narrow window. */}
        <div
          onPointerDown={dragStart}
          onPointerMove={dragMove}
          onPointerUp={dragEnd}
          onPointerCancel={dragEnd}
          className="shrink-0 touch-none"
        >
          <div className="flex justify-center pt-2.5 pb-1.5">
            <span className="h-1 w-10 rounded-full bg-ink-300" />
          </div>
          <div className="flex items-center gap-3 border-b border-ink-200 px-4 pb-2.5">
            {header ? (
              <div className="flex min-w-0 flex-1 items-center gap-2">{header}</div>
            ) : title ? (
              <h2 className="min-w-0 flex-1 truncate text-[15px] font-semibold text-ink-900">
                {title}
              </h2>
            ) : (
              <span className="flex-1" />
            )}
            <button
              type="button"
              onClick={close}
              className="shrink-0 rounded-lg bg-ink-100 px-3.5 py-1.5 text-[13px] font-semibold text-ink-700 transition-colors hover:bg-ink-200"
            >
              Done
            </button>
          </div>
        </div>

        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col',
            // A `tall` sheet's children pin their own bar over the home
            // indicator; an `auto` one ends where its content does, so the
            // padding belongs to the sheet.
            scrollBody && 'overflow-y-auto overscroll-contain',
            size === 'auto' && 'pb-[var(--safe-bottom)]',
          )}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}
